<script setup lang="ts">
import { computed, onMounted, useTemplateRef } from 'vue';

import { useGlobalStyle } from '@/store/templateData';
import { isUrl } from '@/utils/validators/common';
import IconFa6SolidImage from '~icons/fa6-solid/image';

defineOptions({
  name: 'ModuleBannerDisplay'
});

interface Props {
  module: {
    components: {
      title: {
        text?: string;
      };
      image: {
        src?: string;
      };
      replaceHyperLink?: {
        url?: string;
      };
    };
  };
}

const props = withDefaults(defineProps<Props>(), {
  module: () => ({
    components: {
      title: { text: '' },
      image: { src: '' },
      replaceHyperLink: { url: '' }
    }
  })
});

const components = computed(() => props.module.components);
const needRender = computed(() => [
  components.value.title.text?.trim(),
  components.value.image.src,
].some(Boolean));

const { titleStyle } = useGlobalStyle();

const bannerImageRef = useTemplateRef('bannerImageRef');

// NOTE:
// 安全地處理橫幅圖片的超連結功能
// 避免使用 v-html 直接渲染不可信的 HTML 內容到 DOM 中
// 使用 document.createElement 來創建安全的連結元素
const triggerReplaceHyperLink = () => {
  const imageSrc = components.value.image.src;
  const linkUrl = components.value?.replaceHyperLink?.url;
  const containerElement = bannerImageRef.value;

  if (!containerElement) {
    return;
  }

  containerElement.innerHTML = '';

  if (!imageSrc) {
    return;
  }

  const imgElement = document.createElement('img');
  imgElement.src = imageSrc;
  imgElement.alt = components.value.title.text ?? '';
  imgElement.style.width = '100%';
  imgElement.style.height = '100%';
  imgElement.style.objectFit = 'cover';

  if (linkUrl && isUrl(linkUrl)) {
    const linkElement = document.createElement('a');
    linkElement.href = linkUrl;
    linkElement.target = '_blank';
    linkElement.rel = 'noopener noreferrer';
    linkElement.style.display = 'block';
    linkElement.style.width = '100%';
    linkElement.style.height = '100%';
    linkElement.style.cursor = 'pointer';
    linkElement.appendChild(imgElement);
    containerElement.appendChild(linkElement);
  } else {
    containerElement.appendChild(imgElement);
  }
};

onMounted(() => {
  triggerReplaceHyperLink();
});

</script>

<template>
<div class="module-banner-display" v-if="needRender">
  <div
    class="module-banner-display__title"
    v-if="components.title.text"
    :style="titleStyle"
  >
    {{ components.title.text }}
  </div>
  <div class="module-banner-display__wrapper">
    <template v-if="components.image.src">
      <div class="module-banner-display__wrapper__image">
        <figure class="w-full h-full" ref="bannerImageRef"></figure>
      </div>
    </template>
    <template v-else>
      <div class="module-banner-display__wrapper__placeholder">
        <IconFa6SolidImage class="text-8xl text-gray-700"/>
        <!---
          span.text-gray-700 {{ $t("cms_setting.display.banner_image_placeholder") }}
        -->
      </div>
    </template>
  </div>
</div>
</template>
<style lang="scss" scoped>
$desktop-fixed-width: 1075px;

.module-banner-display {
  @apply py-4;

  &__title {
    @apply mb-4 font-medium break-all;
    @apply text-4xl text-center whitespace-pre-line;
    // mobile版圖片滿版，但文字要與其他 module 等寬 (留 20px 的 padding)
    @apply px-5;

    word-wrap: break-word;
  }

  &__wrapper {
    @apply relative my-6 bg-gray-600;

    @include aspect-ratio($height: 1, $width: 2);

    &__image,
    &__placeholder {
      @apply absolute inset-0 w-full h-full;
    }

    &__placeholder {
      @apply flex flex-col items-center justify-center;
    }
  }
}

// tablet
.tablet .module-banner-display {
  @apply px-10;

  &__title {
    @apply px-0;
  }
}

// PC - 1024px
.laptop .module-banner-display {
  padding-left: 10%;
  padding-right: 10%;

  @apply py-8;

  &__title {
    @apply px-0;
  }
}

// PC -1440px
.desktop .module-banner-display {
  width: $desktop-fixed-width;

  @apply py-8;

  &__title {
    @apply px-0;
  }
}
</style>
